import React from 'react';
import {useForm} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import {tariffAPI, ITariff, ITariffVariant, getTariffOption, getTariffVariantOption} from 'entities/tariff';
import {tariffVariantAPI} from 'features/admin/api/tariffVariantAPI';
import {paymentAPI} from 'entities/payment';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import EmailInput from 'shared/ui/Input/InputEmail';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import Select from 'shared/ui/Select/Select';
import DatePickerControlled from 'shared/ui/Date/DateRangePickerControlled/DatePickerControlled';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';

interface IPaymentsForm {
    userEmail: string;
    tariff: string;
    tariffVariant: string | null;
    paidAt: Date;
    needActivate: boolean;
}

interface IPaymentsFormProps {
    onClose: () => void;
}

const PaymentsForm: React.FC<IPaymentsFormProps> = ({onClose}) => {
    const {register, handleSubmit, formState, control} = useForm<IPaymentsForm>({mode: 'onChange'});
    const {errors, isValid, isDirty} = formState;
    const {data: tariffs} = tariffAPI.useGetTariffsQuery({field: 'price', direction: 'asc'});
    const {data: tariffsVariants} = tariffVariantAPI.useGetTariffVariantsQuery({field: 'price', direction: 'asc'});
    const [addManualPayment] = paymentAPI.useAddManualPaymentMutation();

    const getTariffOptionWithArchive = (tariff: ITariff) => {
        const option = getTariffOption(tariff);
        return {
            ...option,
            label: tariff.is_archive ? `${option.label} (арх.)` : option.label,
        };
    };

    const getVariantTariff = (variantTariff: ITariffVariant) => {
        const option = getTariffVariantOption(variantTariff);
        return {...option};
    };

    const onSubmit = async(data: IPaymentsForm) => {
        const isNullVariant = data.tariffVariant?.endsWith('/0');
        const paymentData = {
            userEmail: data.userEmail,
            tariff: data.tariff,
            tariffVariant: isNullVariant ? null : data.tariffVariant,
            paidAt: data.paidAt.toISOString(),
            needActivate: data.needActivate,
        };
        await addManualPayment(paymentData as any).unwrap();
        onClose();
    };


    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    label="E-mail пользователя"
                    error={errors?.userEmail?.message}
                >
                    <EmailInput
                        name="userEmail"
                        register={register}
                        control={control}
                        required
                        placeholder="Введите email"
                    />
                </FormGroupWithError>
                <FormGroupWithError
                    error={errors?.tariff?.message}
                    label="Тариф"
                >
                    {
                        tariffs
                        && <Select
                            rules={{required: 'Выберите тариф'}}
                            control={control}
                            name="tariff"
                            cleanable
                            data={tariffs.map(getTariffOptionWithArchive)}
                            placeholder="Выберите тариф"
                            searchable={false}
                        />
                    }
                </FormGroupWithError>
                <FormGroupWithError
                    error={errors?.tariffVariant?.message}
                    label="Вариант тарифа"
                >
                    {tariffsVariants && (
                        <Select
                            rules={{required: 'Выберите вариант тарифа'}}
                            control={control}
                            name="tariffVariant"
                            cleanable
                            data={tariffsVariants.map(getVariantTariff)}
                            placeholder="Выберите вариант тарифа"
                            searchable={false}
                        />
                    )}
                </FormGroupWithError>
                <FormGroupWithError
                    error={errors?.paidAt?.message}
                    label="Выберите дату платежа"
                >
                    <DatePickerControlled
                        control={control}
                        name="paidAt"
                        cleanable
                        oneTap
                        rules={{required: 'Выберите дату'}}
                        placeholder="Выберите дату платежа"
                    />
                </FormGroupWithError>
                <FormGroupWithError>
                    <Checkbox
                        id="needActivate"
                        {...register('needActivate')}
                    >
                        <span>Включить тариф</span>
                    </Checkbox>
                </FormGroupWithError>
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={t`Добавить`}
                primaryButtonType="submit"
                primaryButtonDisabled={!isValid || !isDirty || !errors}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default PaymentsForm;